import { useState, type FormEvent } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Plus, Trash2, ChevronRight, AlertCircle } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { formatCurrency, formatDate, cn } from '../utils/formatters';
import { input, label, btnGhost, modalOverlay, modalContent, modalBody, modalFooter } from '../styles/components';
import { ProjectPhotos } from '../components/ProjectPhotos';
import { ProjectDocuments } from '../components/ProjectDocuments';
import type { Expense } from '../types';

const statusConfig: Record<string, { label: string; pill: string }> = {
  planned: { label: 'Planificado', pill: '' },
  in_progress: { label: 'En curso', pill: 'b2b-pill--accent' },
  stopped: { label: 'Detenido', pill: 'b2b-pill--warn' },
  finished: { label: 'Finalizado', pill: 'b2b-pill--ok' },
};

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { projects, clients, budgets, expenses, createExpense, deleteProject } = useCompany();
  const navigate = useNavigate();
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [form, setForm] = useState({ category: 'material', description: '', amount: '', vatRate: '21', supplier: '', notes: '' });

  const project = projects.find(p => p.id === id);

  if (!project) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-danger-500" />
          <h1 className="text-2xl font-bold text-danger-600">Proyecto no encontrado</h1>
          <p className="b2b-muted mb-4">El proyecto con ID {id} no existe.</p>
          <button onClick={() => navigate('/projects')} className="b2b-btn b2b-btn--primary">
            <Plus className="w-4 h-4" /> Ver proyectos
          </button>
        </div>
      </div>
    );
  }

  const client = clients.find(c => c.id === project.clientId);
  const budget = budgets.find(b => b.id === project.budgetId || b.projectId === project.id);
  const projectExpenses = expenses.filter(e => e.projectId === project.id);
  const totalInvested = projectExpenses.reduce((sum, e) => sum + e.total, 0);
  const status = statusConfig[project.status] || statusConfig.planned;

  const handleAddExpense = async (e: FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(form.amount) || 0;
    const data: Omit<Expense, 'id' | 'companyId' | 'createdAt' | 'updatedAt' | 'vatAmount' | 'total'> = {
      projectId: project.id,
      category: form.category as Expense['category'],
      description: form.description,
      amount,
      vatRate: parseInt(form.vatRate) || 21,
      date: new Date().toISOString().split('T')[0],
      supplier: form.supplier,
      notes: form.notes,
    };
    await createExpense(data);
    setShowAddExpense(false);
    setForm({ category: 'material', description: '', amount: '', vatRate: '21', supplier: '', notes: '' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Proyecto</span>
          <h1 className="text-3xl font-extrabold tracking-tight">{project.name}</h1>
          <p className="b2b-muted">{client?.name || 'Sin cliente'}</p>
        </div>
        <span className={cn('b2b-pill', status.pill)}>{status.label}</span>
      </div>

      <div className="b2b-panel">
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="b2b-card__label">Cliente</p>
            <p className="text-lg font-semibold">{client?.name || '—'}</p>
          </div>
          <div>
            <p className="b2b-card__label">Dirección</p>
            <p className="text-lg font-semibold">{project.address || '—'}</p>
          </div>
          <div>
            <p className="b2b-card__label">Fecha inicio</p>
            <p className="text-lg font-semibold">{project.startDate ? formatDate(project.startDate) : '—'}</p>
          </div>
          <div>
            <p className="b2b-card__label">Fecha fin</p>
            <p className="text-lg font-semibold">{project.endDate ? formatDate(project.endDate) : '—'}</p>
          </div>
          <div>
            <p className="b2b-card__label">Días estimados</p>
            <p className="text-lg font-semibold">{project.estimatedDays || 0} días</p>
          </div>
          <div>
            <p className="b2b-card__label">Presupuesto</p>
            {budget ? (
              <Link to={`/budgets/${budget.id}`} className="text-lg font-semibold text-[color:var(--b2b-accent-strong)] inline-flex items-center gap-1">
                {budget.number} <ChevronRight className="w-4 h-4" />
              </Link>
            ) : (
              <p className="text-lg font-semibold">Sin presupuesto</p>
            )}
          </div>
          {project.description && (
            <div className="sm:col-span-2 lg:col-span-3">
              <p className="b2b-card__label">Descripción</p>
              <p className="b2b-muted">{project.description}</p>
            </div>
          )}
        </div>
      </div>

      {budget && (
        <div className="b2b-bento">
          <div className="b2b-card b2b-col-4">
            <span className="b2b-card__label">Presupuestado</span>
            <span className="b2b-card__value">{formatCurrency(budget.total)}</span>
          </div>
          <div className="b2b-card b2b-col-4">
            <span className="b2b-card__label">Invertido</span>
            <span className="b2b-card__value">{formatCurrency(totalInvested)}</span>
          </div>
          <div className="b2b-card b2b-card--accent b2b-col-4">
            <span className="b2b-card__label">Margen</span>
            <span className="b2b-card__value">{formatCurrency(budget.total - totalInvested)}</span>
          </div>
        </div>
      )}

      <div className="b2b-panel">
        <div className="b2b-panel__head">
          <h2 className="text-lg font-semibold">Gastos del proyecto</h2>
          <button onClick={() => setShowAddExpense(true)} className="b2b-btn b2b-btn--primary b2b-btn--sm">
            <Plus className="w-4 h-4" /> Añadir gasto
          </button>
        </div>
        {projectExpenses.length === 0 ? (
          <div className="p-8 text-center b2b-muted">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No hay gastos registrados</p>
          </div>
        ) : (
          <>
            {projectExpenses.map(exp => (
              <div key={exp.id} className="b2b-panel__row">
                <span className="min-w-0 flex-1">
                  <span className="block font-medium truncate">{exp.description || '—'}</span>
                  <span className="block text-xs b2b-muted">{formatDate(exp.date)} · {exp.supplier || '—'}</span>
                </span>
                <span className="b2b-pill">IVA {exp.vatRate}%</span>
                <span className="font-semibold whitespace-nowrap">{formatCurrency(exp.total)}</span>
              </div>
            ))}
            <div className="b2b-panel__row" style={{ background: 'var(--b2b-surface-muted)', justifyContent: 'space-between' }}>
              <span className="b2b-muted">Inversión total</span>
              <span className="font-semibold">{formatCurrency(totalInvested)}</span>
            </div>
          </>
        )}
      </div>

      <ProjectPhotos projectId={project.id} />

      <ProjectDocuments projectId={project.id} />

      <div className="pt-6 border-t border-[var(--b2b-border)]">
        <button onClick={() => setShowDeleteModal(true)} className="b2b-btn b2b-btn--danger">
          <Trash2 className="w-4 h-4" /> Eliminar proyecto
        </button>
      </div>

      {showAddExpense && (
        <div className={modalOverlay}>
          <div className={cn(modalContent, 'max-w-md')} role="dialog" aria-modal="true">
            <form onSubmit={handleAddExpense}>
              <div className={modalBody}>
                <h2 className="text-xl font-bold mb-4">Registrar Gasto</h2>
                <div className="space-y-3">
                  <div>
                    <label className={label}>Descripción *</label>
                    <input className={input} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>Categoría</label>
                      <select className={input} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                        <option value="material">Materiales</option>
                        <option value="tool">Herramientas</option>
                        <option value="transport">Transporte</option>
                        <option value="subcontract">Subcontratación</option>
                        <option value="other">Otros</option>
                      </select>
                    </div>
                    <div>
                      <label className={label}>Importe (sin IVA) *</label>
                      <input type="number" min="0" step="0.01" className={input} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>IVA</label>
                      <select className={input} value={form.vatRate} onChange={e => setForm({ ...form, vatRate: e.target.value })}>
                        <option value="21">21% (General)</option>
                        <option value="10">10% (Rehabilitación)</option>
                      </select>
                    </div>
                    <div>
                      <label className={label}>Proveedor</label>
                      <input className={input} value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className={label}>Notas</label>
                    <textarea className={input} rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className={modalFooter}>
                <button type="button" onClick={() => setShowAddExpense(false)} className={btnGhost}>Cancelar</button>
                <button type="submit" className="b2b-btn b2b-btn--primary">Guardar gasto</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className={modalOverlay}>
          <div className={cn(modalContent, 'max-w-md p-8')} role="dialog" aria-modal="true">
            <h2 className="text-xl font-bold text-center mb-2">Cuidado</h2>
            <p className="b2b-muted mb-8 text-center">¿Eliminar este proyecto y sus datos asociados?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowDeleteModal(false)} className={btnGhost}>Cancelar</button>
              <button
                onClick={() => {
                  deleteProject(project.id);
                  navigate('/projects');
                }}
                className="b2b-btn b2b-btn--danger"
              >
                <Trash2 className="w-4 h-4" /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
